/**
 * Tiny typed fetch helper for the IAM browser API.
 *
 * All calls go to the same origin as the SPA (Express in prod, the Vite
 * dev proxy locally) and carry a Clerk session JWT in the
 * `Authorization` header. The server's `requireOrgAdmin` verifies the
 * token and extracts org context from the verified claims, so the
 * client never has to send orgId itself.
 *
 * On non-2xx the server always returns
 * `{ error, code, requestId, ... }`. We surface that shape via
 * `ApiError` so dialogs can show the structured `code` (e.g.
 * "TARGET_NOT_ORG_MEMBER") in toasts without parsing strings.
 */

export type ToolRole =
  | "tool-admin"
  | "tool-manager"
  | "tool-member"
  | "tool-viewer"
  | "denied"
  | "none";

export type AssignableToolRole = Exclude<ToolRole, "none">;

export type RoleSource =
  | "super"
  | "admin"
  | "direct"
  | "user-grant"
  | "group"
  | "org"
  | "tier"
  | "cross-org"
  | "none";

export interface Member {
  clerkUserId: string;
  displayName: string | null;
  email: string | null;
  imageUrl: string | null;
  orgRole: string;
  toolRole: ToolRole;
  toolRoleSource?: RoleSource;
  toolRoleGrantedBy?: string | null;
  toolRoleGrantedByName?: string | null;
}

export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string | undefined;
  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

type GetToken = () => Promise<string | null>;

async function authedFetch(
  getToken: GetToken,
  input: string,
  init: RequestInit = {},
  orgId?: string
): Promise<Response> {
  const token = await getToken();
  if (!token) {
    throw new ApiError(401, "AUTH_REQUIRED", "Not signed in");
  }
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  // Always send the active org ID so the server can verify membership via
  // Clerk's API even when the JWT was issued before the org was activated
  // in this browser session (Clerk only embeds org claims if the org was
  // active at token-mint time, which is often not the case on first load).
  if (orgId) headers.set("X-Org-Id", orgId);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(input, { ...init, headers });
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const code =
      (body && typeof body === "object" && typeof body.code === "string"
        ? body.code
        : null) ?? `HTTP_${res.status}`;
    const message =
      (body && typeof body === "object" && typeof body.error === "string"
        ? body.error
        : null) ?? res.statusText ?? "Request failed";
    const requestId =
      body && typeof body === "object" && typeof body.requestId === "string"
        ? body.requestId
        : undefined;
    throw new ApiError(res.status, code, message, requestId);
  }
  return (body ?? {}) as T;
}

function safeJson(text: string): Record<string, unknown> | null {
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export async function listMembers(
  getToken: GetToken,
  productId: string,
  orgId: string
): Promise<Member[]> {
  const res = await authedFetch(
    getToken,
    `/org/tool-access/members?productId=${encodeURIComponent(productId)}`,
    {},
    orgId
  );
  const body = await parseOrThrow<{ members: Member[] }>(res);
  return body.members;
}

export async function grantToolAccess(
  getToken: GetToken,
  args: { clerkUserId: string; productId: string; role: AssignableToolRole },
  orgId: string
): Promise<void> {
  const res = await authedFetch(
    getToken,
    `/org/tool-access/grant`,
    { method: "POST", body: JSON.stringify(args) },
    orgId
  );
  await parseOrThrow<{ ok: true }>(res);
}

export async function revokeToolAccess(
  getToken: GetToken,
  args: { clerkUserId: string; productId: string },
  orgId: string
): Promise<void> {
  const res = await authedFetch(
    getToken,
    `/org/tool-access/grant`,
    { method: "DELETE", body: JSON.stringify(args) },
    orgId
  );
  await parseOrThrow<{ ok: true }>(res);
}
